import type { ReportAggregation } from "@glorify-llc/accounting-core";
import { safeParseDateString } from "@/lib/date-utils";

export interface BankTransaction {
  id: string;
  bankAccount: string;
  transactionDate: Date;
  description: string;
  amount: number;
  status: "reconciled" | "unreconciled" | "pending";
}

export type BankReconciliationReportContext = {
  asOf: Date;
  transactions: BankTransaction[];
  /** Optional book balance from the GL. When provided, displayed as-is. */
  bookBalance?: number;
  /** Optional bank-reported balance. When provided, displayed as-is. */
  bankBalance?: number;
};

// ---------------------------------------------------------------------------
// Raw-row helper — same shape contract as PR #1540 (AR aging) so the
// upstream query result can be funneled into this adapter without each
// case in `useReportData` open-coding the bucketing.
// ---------------------------------------------------------------------------

/**
 * Raw row shape from the `finance_bank_transactions` query, including the
 * joined `finance_bank_accounts` row for display. Field names match the DB
 * column names (snake_case) and are typed loosely so PostgREST string-
 * serialised numerics still pass through.
 */
export interface RawBankTransactionRow {
  id: string;
  bank_account_id: string | null;
  posted_on: string | null;
  description: string | null;
  amount: number | string | null;
  status: string | null;
  reconciled_at: string | null;
  finance_bank_accounts?: { id?: string; account_name?: string } | null;
}

/**
 * Coerce upstream status string to the typed enum. Status comparison is
 * case-insensitive — DB rows have historically held mixed case from imported
 * processor data ("Reconciled", "RECONCILED"). Anything not matching a known
 * status falls through to "unreconciled" so we never silently DROP a row
 * from the totals; if it's truly unknown, it shows up in the unreconciled
 * bucket where a user will notice it.
 */
function normalizeStatus(
  status: unknown,
  reconciledAt: string | null,
): BankTransaction["status"] {
  if (typeof status === "string") {
    const s = status.trim().toLowerCase();
    if (s === "reconciled" || s === "matched" || s === "cleared") return "reconciled";
    if (s === "pending" || s === "in_progress" || s === "in progress") return "pending";
    if (s === "unreconciled" || s === "unmatched" || s === "open") return "unreconciled";
  }
  // Fallback: if status is missing/unknown, infer from `reconciled_at` —
  // a non-null timestamp on this column is the canonical signal that the
  // row was reconciled, regardless of the status column's contents.
  if (reconciledAt) return "reconciled";
  return "unreconciled";
}

function toFiniteNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Convert a raw `finance_bank_transactions` row into the typed shape this
 * adapter expects. Returns `null` for rows missing essential fields (id or
 * unparseable `posted_on`) so they don't silently mis-classify into "today".
 */
export function bucketBankTransactionRow(
  raw: RawBankTransactionRow,
): BankTransaction | null {
  if (!raw || !raw.id) return null;

  if (!raw.posted_on) return null;
  // posted_on is a date-only column. new Date("2026-05-01") is UTC midnight, which the report
  // runner then formats in local time as Apr 30 in the US; read it as that calendar day.
  const date = safeParseDateString(raw.posted_on);
  if (!date) return null;

  const account = raw.finance_bank_accounts ?? null;
  const accountName = account?.account_name || raw.bank_account_id || "Unknown Account";

  return {
    id: raw.id,
    bankAccount: accountName,
    transactionDate: date,
    description: raw.description ?? "",
    amount: toFiniteNumber(raw.amount),
    status: normalizeStatus(raw.status, raw.reconciled_at),
  };
}

// ---------------------------------------------------------------------------

export function buildBankReconciliationReportAggregation(
  context: BankReconciliationReportContext,
): ReportAggregation {
  const transactions = Array.isArray(context.transactions) ? context.transactions : [];

  const rows = transactions
    .map((transaction) => ({
      id: transaction.id,
      label: transaction.description,
      entityType: "task" as const,
      drilldownUrl: `/finance/banking?txn=${encodeURIComponent(transaction.id)}`,
      bankAccount: transaction.bankAccount,
      transactionDate: transaction.transactionDate,
      description: transaction.description,
      amount: roundCents(toFiniteNumber(transaction.amount)),
      status: transaction.status,
    }))
    // Most recent first — deterministic order, ties broken by id.
    .sort((a, b) => {
      const at = a.transactionDate instanceof Date ? a.transactionDate.getTime() : 0;
      const bt = b.transactionDate instanceof Date ? b.transactionDate.getTime() : 0;
      if (at !== bt) return bt - at;
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    });

  // Single-pass totals (replaces 4 array walks: 2 filters + 2 reduces).
  let totalReconciled = 0;
  let totalUnreconciled = 0;
  let totalPending = 0;
  for (const t of transactions) {
    const amount = toFiniteNumber(t.amount);
    if (t.status === "reconciled") totalReconciled += amount;
    else if (t.status === "unreconciled") totalUnreconciled += amount;
    else if (t.status === "pending") totalPending += amount;
  }
  totalReconciled = roundCents(totalReconciled);
  totalUnreconciled = roundCents(totalUnreconciled);
  totalPending = roundCents(totalPending);

  // CRITICAL: do NOT default `bookBalance` / `bankBalance` to `totalReconciled`.
  // The whole purpose of the reconciliation report is to surface when the
  // two balances disagree. Defaulting both to the same derived value masked
  // missing inputs and made the report look perpetually "in balance" when
  // upstream data was unavailable. Surface `null` instead so the renderer
  // can show "Not provided" / em-dash explicitly.
  const bookBalance = context.bookBalance != null && Number.isFinite(context.bookBalance)
    ? roundCents(context.bookBalance)
    : null;
  const bankBalance = context.bankBalance != null && Number.isFinite(context.bankBalance)
    ? roundCents(context.bankBalance)
    : null;
  const difference =
    bookBalance != null && bankBalance != null
      ? roundCents(bookBalance - bankBalance)
      : null;

  return {
    asOf: context.asOf,
    summary: [
      { label: "Total Reconciled", value: totalReconciled },
      { label: "Total Unreconciled", value: totalUnreconciled },
      { label: "Total Pending", value: totalPending },
      // `null` here is preserved through the report-types contract so the
      // renderer can distinguish "no data" from "$0".
      { label: "Book Balance", value: bookBalance ?? "—" },
      { label: "Bank Balance", value: bankBalance ?? "—" },
      { label: "Difference", value: difference ?? "—" },
    ],
    rows,
  };
}
